import { BalanceData } from "../data/balance";
import { ResearchNodeDef, ResearchStat } from "../data/research";
import { Fix64 } from "../maths/fix64";
import { ownedTowers } from "../sim/ownership";
import { GearBonus } from "./gear";
import type { PlayerProfile } from "./profile";

/**
 * Gold Research (M35, docs/RESEARCH.md): permanent per-tower and Fortress perks bought once with gold, Kingdom Rush
 * style. A node is open when its tree's persistent level (the tower type's, or the Fortress's) has reached its gate and
 * every node it requires is owned; buying is instant (no builder). Combat reaches a level ONLY through the profile's
 * per-archetype GearBonus (`withResearch`, folded beside VIP's in PlayerProfile.gearBonuses): fixed for the run and
 * hashed like gear. With nothing researched the bonuses come back untouched, so every no-research run hashes as before.
 * The owned set is the `research` save section (@highmarch/app research.ts), not a profile field.
 */

/** The JSON of the `research` save section. */
export interface ResearchJson { owned: string[] }

/** The nodes a player owns, by id, kept sorted (no Set iteration order anywhere near the sim). */
export class ResearchBook {
  private ids: string[] = [];

  has(id: string): boolean { return this.ids.includes(id); }
  get owned(): readonly string[] { return this.ids; }
  get count(): number { return this.ids.length; }

  /** Adds `id` (a no-op when owned). The rules (`buyResearch`) decide whether it may be. */
  add(id: string): void {
    if (this.has(id)) return;
    this.ids.push(id);
    this.ids.sort();
  }

  clear(): void { this.ids = []; }

  /**
   * Replaces the book with a saved one: only ids the bundle knows (an id a newer build added is dropped, never
   * guessed at), each once. Anything that is not `{ owned: string[] }` reads as nothing researched (an old save).
   */
  load(balance: BalanceData, j: unknown): void {
    this.clear();
    const owned = typeof j === "object" && j !== null && Array.isArray((j as ResearchJson).owned) ? (j as ResearchJson).owned : [];
    const known = new Set(balance.research.nodes.map((n) => n.id));
    for (const id of owned) if (typeof id === "string" && known.has(id)) this.add(id);
  }

  toJson(): ResearchJson { return { owned: [...this.ids] }; }

  static of(ids: readonly string[]): ResearchBook {
    const b = new ResearchBook();
    for (const id of ids) b.add(id);
    return b;
  }
}

export function researchNode(balance: BalanceData, id: string): ResearchNodeDef | null {
  return balance.research.nodes.find((n) => n.id === id) ?? null;
}

/** The nodes of one tree (a tower archetype index, the base's for the Fortress), in tree order. */
export function researchTree(balance: BalanceData, towerIndex: number): ResearchNodeDef[] {
  return balance.research.nodes.filter((n) => n.towerIndex === towerIndex);
}

/** The persistent level a tree's gate reads: the Fortress's for the base's tree, else the tower type's. */
export function researchLevelOf(profile: PlayerProfile, towerIndex: number): number {
  return profile.balance.towers[towerIndex]?.isBase ? profile.baseLevel : (profile.towerLevels[towerIndex] ?? 1);
}

export type ResearchState = "owned" | "available" | "locked";

/** One node as the research screen and the summary see it. */
export interface ResearchNodeView {
  readonly node: ResearchNodeDef;
  readonly state: ResearchState;
  /** Locked: what is missing first ("Needs Keen Edge", "Archer level 6"); null otherwise. */
  readonly lockedBy: string | null;
  /** Available and the player has the gold. */
  readonly affordable: boolean;
}

/** Whether `node` is owned, open to buy, or locked (and by what). */
export function researchView(profile: PlayerProfile, node: ResearchNodeDef): ResearchNodeView {
  const B = profile.balance, book = profile.research;
  if (book.has(node.id)) return { node, state: "owned", lockedBy: null, affordable: false };
  const missing = node.requires.find((r) => !book.has(r));
  if (missing !== undefined) return { node, state: "locked", lockedBy: `Needs ${researchNode(B, missing)?.name ?? missing}`, affordable: false };
  const t = B.towers[node.towerIndex];
  if (researchLevelOf(profile, node.towerIndex) < node.gate) return { node, state: "locked", lockedBy: `${t.isBase ? "Fortress" : t.name} level ${node.gate}`, affordable: false };
  return { node, state: "available", lockedBy: null, affordable: profile.gold >= node.gold };
}

/** Buys a node with gold: it must exist, not be owned, be open (gate and requirements) and be affordable. */
export function buyResearch(profile: PlayerProfile, id: string): ResearchNodeDef | string {
  const node = researchNode(profile.balance, id);
  if (node === null) return "no such research";
  const v = researchView(profile, node);
  if (v.state === "owned") return "already researched";
  if (v.state === "locked") return "locked";
  if (!v.affordable) return "not enough gold";
  profile.gold -= node.gold;
  profile.research.add(node.id);
  return node;
}

/** What one archetype's owned research adds, as fractions per stat (zero when nothing). */
export function researchBonusFor(balance: BalanceData, book: ResearchBook, towerIndex: number): Record<ResearchStat, Fix64> {
  const out: Record<ResearchStat, Fix64> = { damage: Fix64.Zero, range: Fix64.Zero, points: Fix64.Zero, hp: Fix64.Zero, troops: Fix64.Zero };
  for (const n of balance.research.nodes) if (n.towerIndex === towerIndex && book.has(n.id)) out[n.stat] = out[n.stat].add(n.value);
  return out;
}

/**
 * A GearBonus fraction with research compounded on top: (1 + g)(1 + r) − 1 = g + r + g·r (zero research: `g` itself).
 * Research multiplies like a tower level does (×1.03 a level), so +4 % is +4 % whatever the gear already adds, and a
 * node's worth in tower levels (research.json) holds at every gear score instead of shrinking as gear grows.
 */
function compound(g: Fix64, r: Fix64): Fix64 { return r.isZero() ? g : g.add(r).add(g.mul(r)); }

/**
 * The per-archetype GearBonus a level runs with, plus owned research, compounded (`compound`): damage, range and kill
 * points on the node's archetype; health and troops on the base's (the Fortress tree; troops hashed only when nonzero,
 * sim/systems/simHash.ts). Nothing researched returns the bonuses untouched, so a no-research run hashes exactly as before.
 */
export function withResearch(balance: BalanceData, bonuses: readonly GearBonus[], book: ResearchBook): GearBonus[] {
  if (book.count === 0) return [...bonuses];
  return bonuses.map((g, i) => {
    const r = researchBonusFor(balance, book, i);
    if (r.damage.isZero() && r.range.isZero() && r.points.isZero() && r.hp.isZero() && r.troops.isZero()) return g;
    const out: GearBonus = { ...g, damage: compound(g.damage, r.damage), range: compound(g.range, r.range), points: compound(g.points, r.points), hp: compound(g.hp, r.hp) };
    return r.troops.isZero() ? out : { ...out, troops: compound(g.troops ?? Fix64.Zero, r.troops) };
  });
}

/**
 * The tower levels one archetype's owned research counts as (each node's `worth`, research.json): the readiness towers
 * meter adds it like VIP's tower damage, so Ready keeps meaning the power the level was tuned for. Presentation
 * arithmetic (floats): never a sim input.
 */
export function researchTowerLevels(balance: BalanceData, book: ResearchBook, towerIndex: number): number {
  let sum = 0;
  for (const n of balance.research.nodes) if (n.towerIndex === towerIndex && book.has(n.id)) sum += n.worth;
  return sum;
}

/**
 * The research a player is expected to own going into campaign level `level` (the expectedInventory of research; the
 * balance report's reference player and docs/RESEARCH.md): every node of the base's tree and of each tower owned by
 * then whose gate is at most level − expectLagLevels, its requirements included. Deterministic.
 */
export function expectedResearch(balance: BalanceData, level: number): ResearchBook {
  const owned = ownedTowers(balance, level - 1);
  const reach = level - balance.research.expectLagLevels;
  const book = new ResearchBook();
  for (const n of balance.research.nodes) {
    const ownsTree = balance.towers[n.towerIndex].isBase || owned[n.towerIndex];
    if (ownsTree && n.gate <= reach && n.requires.every((r) => book.has(r))) book.add(n.id);
  }
  return book;
}
